import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, Mic, Square } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import LanguageSelector from "@/components/LanguageSelector";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyDetails = {
  founder_name: "", company_name: "", mobile_number: "", email: "",
  city: "", state: "", monthly_turnover_range: "",
};

const slide = {
  enter: (d) => ({ x: d > 0 ? 70 : -70, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d > 0 ? -70 : 70, opacity: 0 }),
};

export default function SurveyFlow() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0); // 0 = details, 1..N = question index
  const [dir, setDir] = useState(1);
  const [details, setDetails] = useState(emptyDetails);
  const [mcq, setMcq] = useState({});
  const [text, setText] = useState({});
  const [voiceMeta, setVoiceMeta] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const advanceRef = useRef(null);

  useEffect(() => {
    api
      .get(`/survey/questions?lang=${lang}`)
      .then(({ data }) => setConfig(data))
      .catch(() => toast.error("Could not load survey. Please refresh."));
    return () => clearTimeout(advanceRef.current);
  }, [lang]);

  const questions = useMemo(() => {
    if (!config) return [];
    return [
      ...(config.mcq || []).map((q) => ({ ...q, type: "mcq" })),
      ...(config.text || []).map((q) => ({ ...q, type: "text" })),
    ];
  }, [config]);

  const totalQ = questions.length;
  const totalSteps = totalQ + 1;
  const progress = Math.round((step / totalSteps) * 100);

  const goNext = () => {
    if (step < totalSteps - 1) {
      setDir(1);
      setStep((s) => s + 1);
    }
  };
  const goBack = () => {
    clearTimeout(advanceRef.current);
    if (step > 0) {
      setDir(-1);
      setStep((s) => s - 1);
    } else navigate("/");
  };

  const validateDetails = () => {
    for (const v of Object.values(details)) if (!v || !String(v).trim()) return false;
    if (!/^\S+@\S+\.\S+$/.test(details.email)) { toast.error(t("err_email")); return false; }
    if (!/^[0-9+\-\s]{7,15}$/.test(details.mobile_number)) { toast.error(t("err_mobile")); return false; }
    return true;
  };

  const onDetailsNext = () => {
    if (!validateDetails()) return toast.error(t("err_details"));
    goNext();
  };

  const pickMcq = (id, letter) => {
    setMcq((m) => ({ ...m, [id]: letter }));
    clearTimeout(advanceRef.current);
    advanceRef.current = setTimeout(() => goNext(), 260);
  };

  const submit = async () => {
    setSubmitting(true);
    const tracking = JSON.parse(sessionStorage.getItem("dos_tracking") || "{}");
    try {
      const { data } = await api.post("/survey/submit", {
        respondent: details,
        mcq_answers: Object.fromEntries(Object.entries(mcq).map(([k, v]) => [String(k), v])),
        text_answers: Object.fromEntries(Object.entries(text).map(([k, v]) => [String(k), v])),
        voice_meta: voiceMeta,
        selected_language: lang,
        meta: { source: tracking.source || "direct", campaign: tracking.campaign || "", referrer: tracking.referrer || "" },
      });
      sessionStorage.removeItem("dos_tracking");
      navigate("/thank-you", { state: { score: data.total_score, category: data.score_category, email: details.email } });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d[0]?.msg || t("submit_failed") : t("submit_failed");
      toast.error(msg);
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0F1C]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const q = step >= 1 ? questions[step - 1] : null;
  const isLast = step === totalQ;
  const midpoint = Math.ceil(totalQ / 2);

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <div className="sticky top-0 z-20 border-b border-white/10 bg-[#0A0F1C]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-5 py-3">
          <button data-testid="back-btn" onClick={goBack} aria-label="Back"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/10">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 text-center text-xs font-medium text-blue-300" data-testid="step-label">
            {step === 0 ? t("step_details") : t("q_counter", { a: step, b: totalQ })}
          </div>
          <LanguageSelector variant="dark" />
        </div>
        <div className="mx-auto max-w-2xl px-5 pb-3">
          <Progress value={progress} className="h-1.5 bg-white/10" data-testid="progress-bar" />
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-5 py-8 sm:py-12">
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div key={step} custom={dir} variants={slide} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}>
            {step === 0 ? (
              <>
                <div className="rounded-3xl bg-white p-6 shadow-2xl shadow-black/40 sm:p-8">
                  <DetailsForm details={details} setDetails={setDetails} turnover={config.turnover_options} t={t} />
                </div>
                <div className="mt-6 flex justify-end">
                  <button data-testid="next-btn" onClick={onDetailsNext}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-8 py-3.5 text-base font-semibold text-white transition-transform hover:-translate-y-0.5">
                    {t("continue")}
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
              </>
            ) : q.type === "mcq" ? (
              <McqScreen q={q} value={mcq[q.id]} onPick={pickMcq}
                encouragement={step === midpoint ? t("halfway") : null} t={t} />
            ) : (
              <TextScreen q={q} value={text[q.id] || ""} t={t} isLast={isLast} submitting={submitting}
                encouragement={step === midpoint ? t("halfway") : null}
                onChange={(val) => setText((s) => ({ ...s, [q.id]: val }))}
                onVoice={(res) => {
                  setText((s) => {
                    const prev = s[q.id] || "";
                    return { ...s, [q.id]: (prev ? prev + " " : "") + res.text };
                  });
                  setVoiceMeta((m) => ({ ...m, [q.id]: { voice_used: true, original_transcript: res.text, detected_language: res.detected_language || "" } }));
                }}
                onContinue={() => {
                  if (!(text[q.id] || "").trim()) return toast.error(t("err_required"));
                  if (isLast) submit();
                  else goNext();
                }} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function Encouragement({ text }) {
  if (!text) return null;
  return (
    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      className="mb-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500/15 to-blue-500/15 px-4 py-1.5 text-sm font-medium text-emerald-300"
      data-testid="encouragement">
      {text}
    </motion.div>
  );
}

function McqScreen({ q, value, onPick, encouragement, t }) {
  return (
    <div data-testid={`question-${q.id}`}>
      <Encouragement text={encouragement} />
      <p className="font-display text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl">
        {q.text}
      </p>
      <p className="mt-3 text-sm text-slate-400">{t("tap_to_pick")}</p>
      <div className="mt-7 space-y-3">
        {["A", "B", "C", "D"].map((letter, i) => {
          const active = value === letter;
          return (
            <motion.button
              key={letter} type="button" data-testid={`q${q.id}-opt-${letter}`}
              onClick={() => onPick(q.id, letter)}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + i * 0.06 }}
              className={`flex min-h-[56px] w-full items-center gap-4 rounded-2xl border-2 px-5 py-4 text-left transition-colors ${
                active ? "border-blue-500 bg-blue-500/10" : "border-white/10 bg-[#0F1629] hover:border-white/25"}`}>
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                active ? "bg-blue-500 text-white" : "bg-white/10 text-slate-300"}`}>
                {letter}
              </span>
              <span className="text-base font-medium text-white">{q.options[letter]}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

function TextScreen({ q, value, onChange, onVoice, onContinue, encouragement, isLast, submitting, t }) {
  const { state, start, stop } = useVoiceRecorder();

  const toggle = async () => {
    if (state === "transcribing") return;
    if (state === "recording") {
      const res = await stop();
      if (res.ok && res.text) onVoice(res);
      else toast.error(t("voice_failed"));
      return;
    }
    try { await start(); } catch (e) { toast.error(t("mic_denied")); }
  };
  const recording = state === "recording";
  const busy = state === "transcribing";

  return (
    <div data-testid={`question-${q.id}`}>
      <Encouragement text={encouragement} />
      <p className="font-display text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl">
        {q.text}
      </p>
      <div className="mt-6">
        <Textarea data-testid={`q${q.id}-text`} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={t("type_answer")} autoFocus
          className="min-h-[140px] resize-none rounded-2xl border-2 border-white/10 bg-[#0F1629] text-base text-white placeholder:text-slate-500 focus-visible:ring-blue-500" />
        <div className="mt-3 flex items-center gap-3">
          <button type="button" data-testid={`q${q.id}-mic`} onClick={toggle} disabled={busy}
            className={`inline-flex min-h-[48px] items-center gap-2 rounded-full px-5 text-sm font-medium transition-colors ${
              recording ? "bg-red-500 text-white" : busy ? "bg-white/10 text-slate-400" : "bg-white/10 text-white hover:bg-white/20"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            {busy ? t("transcribing") : recording ? t("listening") : t("tap_speak")}
          </button>
          {recording && <span className="flex h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" />}
        </div>
        <p className="mt-2 text-xs text-slate-500">{t("voice_hint")}</p>
      </div>
      <div className="mt-8 flex justify-end">
        <button data-testid={isLast ? "submit-survey-btn" : "next-btn"} onClick={onContinue} disabled={submitting}
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-8 py-3.5 text-base font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : isLast ? <Check className="h-5 w-5" /> : <ArrowRight className="h-5 w-5" />}
          {isLast ? t("submit") : t("continue")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  );
}

function DetailsForm({ details, setDetails, turnover, t }) {
  const up = (k) => (e) => setDetails((d) => ({ ...d, [k]: e.target.value }));
  const upVal = (k) => (v) => setDetails((d) => ({ ...d, [k]: v }));
  const cls = "bg-white text-slate-900 border-slate-300 focus-visible:ring-blue-500";
  return (
    <div>
      <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">{t("details_title")}</h2>
      <p className="mt-2 text-sm text-slate-500">{t("details_sub")}</p>
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label={t("f_founder")}><Input data-testid="input-founder_name" className={cls} value={details.founder_name} onChange={up("founder_name")} /></Field>
        <Field label={t("f_company")}><Input data-testid="input-company_name" className={cls} value={details.company_name} onChange={up("company_name")} /></Field>
        <Field label={t("f_mobile")}><Input data-testid="input-mobile_number" className={cls} value={details.mobile_number} onChange={up("mobile_number")} placeholder="98765 43210" /></Field>
        <Field label={t("f_email")}><Input data-testid="input-email" type="email" className={cls} value={details.email} onChange={up("email")} placeholder="you@company.com" /></Field>
        <Field label={t("f_city")}><Input data-testid="input-city" className={cls} value={details.city} onChange={up("city")} /></Field>
        <Field label={t("f_state")}><Input data-testid="input-state" className={cls} value={details.state} onChange={up("state")} /></Field>
        <div className="sm:col-span-2">
          <Field label={t("f_turnover")}>
            <Select value={details.monthly_turnover_range} onValueChange={upVal("monthly_turnover_range")}>
              <SelectTrigger data-testid="select-monthly_turnover_range" className={cls}><SelectValue placeholder={t("select_turnover")} /></SelectTrigger>
              <SelectContent>{turnover.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
            </Select>
          </Field>
        </div>
      </div>
    </div>
  );
}
